import { IconExternalLink } from '../icons';

export interface InAppBrowserWarningProps {
  /** Title shown above the message. Default: `'Open in your browser'`. */
  title?: string;
  /**
   * Body copy. Default explains that the current in-app browser can't run
   * the game and asks the player to open it in their device's browser.
   */
  message?: string;
}

/**
 * Full-screen block shown instead of the game when SlotUI detects it's
 * running inside a messaging/social app's in-app browser (see
 * `webviewDetect.ts`'s `isInAppBrowser`). Those WebViews frequently can't
 * reliably run a WebGL game — trying anyway tends to surface as a blank
 * canvas, dead audio, or a broken fullscreen prompt, which is a worse
 * experience than telling the player up front what's wrong and how to
 * fix it. There's no dismiss/retry action: the fix is outside the page
 * (the app's own "Open in Browser" menu item), so there's nothing here to
 * close back into.
 */
export function InAppBrowserWarning({
  title = 'Open in your browser',
  message = 'This game can’t run inside this app’s built-in browser. Tap the menu (⋯ or ⋮) and choose “Open in Browser” to continue.',
}: InAppBrowserWarningProps) {
  return (
    <div
      className="reelay-slot fullscreen"
      role="alert"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        padding: '32px 28px',
        textAlign: 'center',
        background: 'var(--bg)',
        color: 'var(--text)',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconExternalLink size={30} />
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 400,
          letterSpacing: 2,
          textTransform: 'uppercase',
          fontFamily: 'Oswald, sans-serif',
          color: 'var(--accent)',
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.5,
          color: 'var(--text-muted)',
          fontFamily: 'Oswald, sans-serif',
          maxWidth: 280,
        }}
      >
        {message}
      </div>
    </div>
  );
}
